import React, { useState, useEffect, useCallback } from 'react';
import {
  Trophy,
  Target,
  TrendingUp,
  Zap,
  Activity,
  Award,
  Star,
  AlertCircle,
  Users,
  Crown,
  Flame,
  Shield
} from 'lucide-react';
import Card from '../common/Card';
import Skeleton from '../common/Skeleton';
import GradeStamp from '../common/GradeStamp';
import { LetterGrade } from '../../utils/gradeEngine';
import { analyticsApi } from '../../services/api';

interface SuperlativeWinner {
  team_id: number;
  team_name: string;
  player_name?: string;
  value: number;
  display: string;
}

interface Superlative {
  award: string;
  description: string;
  winner: SuperlativeWinner;
}

interface SuperlativesResponse {
  superlatives: Superlative[];
  summary: {
    total_awards: number;
    league_id: number | null;
    teams_analyzed: number;
  };
}

interface SeasonSuperlativesProps {
  leagueId: number;
}

const awardIcons: Record<string, React.ComponentType<any>> = {
  'Highest Single Week Score': Flame,
  'Most Consistent': Shield,
  'Boom or Bust': Zap,
  'Best Regular Season Record': Crown,
  'Strongest Playoff Run': Trophy,
  'Draft Boom of the Year': Star,
  'Biggest Draft Bust': AlertCircle,
  'Late Round Lottery Winner': Target,
  'First Round Failure': TrendingUp,
  'Most Points Against': Users,
  'Should Have Made Playoffs': Activity,
  'Unluckiest Loss': Award,
};

// Roast-voiced display names. Backend award strings stay untouched — this is
// purely a presentation-layer rename.
const awardDisplayNames: Record<string, string> = {
  'Highest Single Week Score': 'Most Explosive Week',
  'Most Consistent': 'The Metronome',
  'Boom or Bust': 'The Chaos Agent',
  'Best Regular Season Record': 'Regular Season Bully',
  'Strongest Playoff Run': 'Built for January',
  'Draft Boom of the Year': 'Steal of the Draft',
  'Biggest Draft Bust': 'Worst Money Spent (Draft Edition)',
  'Late Round Lottery Winner': 'Dumpster-Dive Champion',
  'First Round Failure': 'First-Round Regret',
  'Most Points Against': 'Most Victimized by the Schedule',
  'Should Have Made Playoffs': 'Robbed in Broad Daylight',
  'Unluckiest Loss': 'Scored Enough to Win. Still Lost.',
};

// The awards nobody wants to win — surfaced first, in red pen.
const SHAME_AWARDS = new Set([
  'Biggest Draft Bust',
  'First Round Failure',
  'Most Points Against',
  'Should Have Made Playoffs',
  'Unluckiest Loss',
]);

// Each award carries the grade it stamps on its winner.
const awardGrades: Record<string, LetterGrade> = {
  'Highest Single Week Score': 'A+',
  'Most Consistent': 'A',
  'Boom or Bust': 'C',
  'Best Regular Season Record': 'A+',
  'Strongest Playoff Run': 'A',
  'Draft Boom of the Year': 'A+',
  'Biggest Draft Bust': 'F',
  'Late Round Lottery Winner': 'A',
  'First Round Failure': 'F',
  'Most Points Against': 'D',
  'Should Have Made Playoffs': 'D+',
  'Unluckiest Loss': 'D',
};

const displayNameFor = (award: string): string => awardDisplayNames[award] ?? award;

// Split a display string into a leading stat (number + immediate unit) and the
// remaining context, without altering any values. e.g.
//   "183.3 points"                       -> { stat: "183.3 points", context: "" }
//   "Bijan Robinson (325.5 pts, pick 1)" -> { stat: "Bijan Robinson", context: "325.5 pts, pick 1" }
//   "±14.1 points"                       -> { stat: "±14.1 points", context: "" }
const splitDisplay = (display: string): { stat: string; context: string } => {
  const paren = display.match(/^(.*?)\s*\((.*)\)\s*$/);
  if (paren) {
    return { stat: paren[1].trim(), context: paren[2].trim() };
  }
  return { stat: display.trim(), context: '' };
};

// Tier 1: large marquee cards, grade stamped
const MarqueeCard: React.FC<{ superlative: Superlative; shame?: boolean }> = ({
  superlative,
  shame = false,
}) => {
  const { stat, context } = splitDisplay(superlative.winner.display);
  const grade = awardGrades[superlative.award] ?? (shame ? 'F' : 'A');

  return (
    <Card
      className={`p-6 h-full flex flex-col border-l-2 ${
        shame ? 'border-l-accent-danger' : 'border-l-accent-primary'
      }`}
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-text-secondary uppercase tracking-wide mb-1 leading-tight">
            {displayNameFor(superlative.award)}
          </h3>
          <p className="text-text-primary font-medium truncate">{superlative.winner.team_name}</p>
        </div>
        <GradeStamp grade={grade} size="md" animate className="flex-shrink-0" />
      </div>
      <div className="mt-auto">
        <p className="font-mono text-text-primary leading-none" style={{ fontSize: '2rem' }}>
          {stat}
        </p>
        {context && <p className="font-mono text-sm text-text-secondary mt-2">{context}</p>}
        {superlative.winner.player_name && !context.includes(superlative.winner.player_name) && stat !== superlative.winner.player_name && (
          <p className="text-sm text-text-secondary mt-1">{superlative.winner.player_name}</p>
        )}
      </div>
    </Card>
  );
};

// Tier 2: compact bordered list rows — award · winner · stat
const AwardRow: React.FC<{ superlative: Superlative }> = ({ superlative }) => {
  const IconComponent = awardIcons[superlative.award] || Award;
  const grade = awardGrades[superlative.award];
  return (
    <div className="flex items-center gap-3 py-3 px-1">
      <IconComponent className="w-4 h-4 text-text-secondary flex-shrink-0" />
      <span className="text-text-primary font-medium min-w-0 flex-1 truncate">
        {displayNameFor(superlative.award)}
      </span>
      <span className="text-text-secondary min-w-0 flex-1 truncate text-right hidden sm:block">
        {superlative.winner.team_name}
      </span>
      <span className="font-mono text-text-primary text-right flex-shrink-0 max-w-[40%] truncate">
        {superlative.winner.display}
      </span>
      {grade && <GradeStamp grade={grade} size="xs" className="flex-shrink-0" />}
    </div>
  );
};

const SeasonSuperlatives: React.FC<SeasonSuperlativesProps> = ({ leagueId }) => {
  const [superlatives, setSuperlatives] = useState<SuperlativesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSuperlatives = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await analyticsApi.getSuperlatives(leagueId);
      setSuperlatives(data);
    } catch (err) {
      setError('Failed to load season superlatives');
      console.error('Superlatives fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchSuperlatives();
  }, [fetchSuperlatives]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64 mb-2" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  if (error || !superlatives) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-accent-danger mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-text-primary mb-2">
          Failed to Load Superlatives
        </h3>
        <p className="text-text-secondary mb-4">{error}</p>
        <button
          onClick={fetchSuperlatives}
          className="px-4 py-2 btn-primary rounded-lg transition-colors"
        >
          Try Again
        </button>
      </Card>
    );
  }

  const shame = superlatives.superlatives.filter((s) => SHAME_AWARDS.has(s.award));
  const fame = superlatives.superlatives.filter((s) => !SHAME_AWARDS.has(s.award));
  const tier1 = fame.slice(0, 3);
  const tier2 = fame.slice(3);

  return (
    <div className="space-y-8">
      {/* Header (no emoji) */}
      <div>
        <h2 className="text-2xl font-semibold text-text-primary">The Awards Ceremony</h2>
        <p className="text-text-secondary mt-1">
          Somebody has to win these. Somebody has to wear them.
        </p>
      </div>

      {/* Hall of Shame — the losers go first. That's the point. */}
      {shame.length > 0 && (
        <section>
          <div className="flex items-baseline gap-3 mb-4">
            <h3 className="text-xl font-bold text-accent-danger tracking-tight">Hall of Shame</h3>
            <p className="text-sm text-text-secondary">Screenshot accordingly.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {shame.map((superlative) => (
              <MarqueeCard key={superlative.award} superlative={superlative} shame />
            ))}
          </div>
        </section>
      )}

      {/* Hall of Fame — marquee awards */}
      <section>
        <div className="flex items-baseline gap-3 mb-4">
          <h3 className="text-xl font-bold text-accent-primary tracking-tight">Hall of Fame</h3>
          <p className="text-sm text-text-secondary">Earned, allegedly.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tier1.map((superlative) => (
            <MarqueeCard key={superlative.award} superlative={superlative} />
          ))}
        </div>
      </section>

      {/* Tier 2 — compact bordered list */}
      {tier2.length > 0 && (
        <Card className="p-4 sm:p-6">
          <div className="divide-y divide-fieldline">
            {tier2.map((superlative) => (
              <AwardRow key={superlative.award} superlative={superlative} />
            ))}
          </div>
        </Card>
      )}

      {/* Summary */}
      <div className="pt-6 border-t border-fieldline">
        <p className="text-text-secondary text-sm">
          {superlatives.summary.total_awards} verdicts handed down across {superlatives.summary.teams_analyzed} teams
        </p>
      </div>
    </div>
  );
};

export default SeasonSuperlatives;
